/**
 * Scale/Edit modal – left config column (info + route + interval + drop options).
 * Renders directly in the column — no card wrappers, matching creator style.
 * For Media Devoted | Created By Ciro Autiero
 */

import { Settings2, Sliders } from 'lucide-react';
import { isShortlinkClickIdStructure, isShortlinkDomainScheme } from '@trafficpulse/shared';

import { SearchableSelect } from '@/components/ui';
import { useDefaultCampaign } from '@/features/smswiser-campaigns/hooks/useDefaultCampaign';
import { useToast } from '@/providers/ToastProvider';
import { RouteAmountSection, StaticTagsPickerSection } from '@/features/smswiser-campaigns/components/drop-modals';
import { DropOptionsSection } from '@/features/smswiser-campaigns/components/drop-modals/sections/drop-options/DropOptionsSection';
import { TimeIntervalPicker } from '@/components/ui';
import { ColumnHeader } from '../../shared';
import { orDash } from '../../utils/orDash';

import type { useScaleDropForm } from '../../useScaleDropForm';
import type { ScaleDropModalProps } from '../../ScaleDropModal.types';

type Form = ReturnType<typeof useScaleDropForm>;

interface Props {
  isEdit: boolean;
  isCreateFromLists?: boolean;
  isMulti: boolean;
  rows: ScaleDropModalProps['rows'];
  form: Form;
  gateways: ScaleDropModalProps['gateways'];
  tlds: ScaleDropModalProps['tlds'];
  isBusy: boolean;
  campaigns?: Array<{ id: number; name: string }>;
  createFromListsWiserId?: number;
  onCreateFromListsWiserIdChange?: (wiserId: number) => void;
  tagsByGatewayId?: Record<string, string[]>;
  tagColors?: Record<string, string>;
  countryKey?: string;
}

export function ScaleOrEditConfigColumn(props: Props) {
  const { isEdit, isCreateFromLists = false, isMulti, rows, form, gateways, tlds, isBusy, campaigns = [], createFromListsWiserId, onCreateFromListsWiserIdChange, tagsByGatewayId, tagColors, countryKey } = props;
  const { defaultCampaignId, setDefaultCampaign } = useDefaultCampaign(countryKey ?? '');
  const { success: toastSuccess } = useToast();

  const handleSaveAsDefault = () => {
    const wid = createFromListsWiserId ?? 0;
    if (wid <= 0) return;
    setDefaultCampaign(wid);
    const name = campaigns.find((c) => c.id === wid)?.name ?? 'Campaign';
    toastSuccess(`${name} saved as default`);
  };
  const drop = form.drop!;

  const scalingLabel = isMulti
    ? rows.map((r) => orDash(r.drop.leadsList)).filter((s) => s !== '—').join(', ') || '—'
    : orDash(drop.leadsList);

  return (
    <div className="flex flex-1 flex-col">
      <ColumnHeader
        icon={Settings2}
        title="Configuration"
        subtitle="Route & Schedule"
        badge={
          <span className="rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-medium text-primary">
            {isEdit ? 'Edit' : isCreateFromLists ? 'Create' : 'Scale'}
          </span>
        }
      />

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden p-3">
        {isCreateFromLists && campaigns.length > 0 && (
          <div>
            <div className="flex items-center justify-between gap-1">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Campaign</label>
              {countryKey && (
                <span className="text-[10px]">
                  {(createFromListsWiserId ?? 0) > 0 ? (
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={handleSaveAsDefault}
                    >
                      Save as default
                    </button>
                  ) : defaultCampaignId != null && campaigns.some((c) => c.id === defaultCampaignId) ? (
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => onCreateFromListsWiserIdChange?.(defaultCampaignId)}
                    >
                      Apply default
                    </button>
                  ) : null}
                </span>
              )}
            </div>
            <SearchableSelect<number>
              options={campaigns.map((c) => ({ value: c.id, label: c.name }))}
              value={createFromListsWiserId ?? campaigns[0]?.id ?? 0}
              onChange={(v) => onCreateFromListsWiserIdChange?.(v === 0 ? campaigns[0]?.id ?? 0 : v)}
              placeholder="Select campaign"
              searchPlaceholder="Search campaigns..."
            />
          </div>
        )}
        <div className="rounded bg-muted/20 px-2 py-1 text-xs">
          <p className="truncate text-foreground" title={scalingLabel}>
            {isEdit ? 'Editing' : isCreateFromLists ? 'Creating' : 'Scaling'}: {scalingLabel}
            {isMulti && !isEdit && ` (${rows.length})`}
          </p>
          <p className="text-muted-foreground">
            {isCreateFromLists ? 'All fields are required.' : `${orDash(drop.smsGatewayRoute)} · ${drop.cap ?? '—'}`}
          </p>
          {isMulti && !form.allowKeepEachGateway && !isEdit && (
            <p className="mt-0.5 text-amber-600 dark:text-amber-500">
              Select a gateway to apply to all.
            </p>
          )}
        </div>

        <RouteAmountSection
          gateways={gateways}
          tlds={tlds}
          routeId={form.routeId}
          setRouteId={form.setRouteId}
          tldValue={form.tldValue}
          setTldValue={form.setTldValue}
          amount={form.amount}
          setAmount={form.setAmount}
          executeAfter={form.executeAfter}
          setExecuteAfter={form.setExecuteAfter}
          costInfo={form.costInfo}
          isBusy={isBusy}
          multiScaleMode={isMulti}
          allowKeepEachGateway={form.allowKeepEachGateway}
          tagsByGatewayId={tagsByGatewayId}
          tagColors={tagColors}
        />

        {isMulti && (
          <div className="space-y-1">
            <label className="block text-xs text-muted-foreground">
              Interval between drops <span className="text-muted-foreground/80">(hh:mm)</span>
            </label>
            <TimeIntervalPicker value={form.scheduleInterval} onChange={form.setScheduleInterval} disabled={isBusy} />
            {!form.executeAfter && form.scheduleIntervalMinutes > 0 && (
              <p className="text-[10px] text-muted-foreground">
                1st drop sends now, next every {form.scheduleInterval} after.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-border/40">
        <div className="flex items-center gap-1.5 bg-muted/20 px-4 py-2">
          <Sliders className="h-3.5 w-3.5 text-primary" />
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Drop Options</span>
        </div>
        <div className="p-3">
          <DropOptionsSection
            hideTitle
            amount={form.amount}
            forceSend={form.forceSend}
            setForceSend={(v) => form.setForceSend(v === 'unchanged' ? false : v)}
            sendInBatches={form.sendInBatches}
            setSendInBatches={(v) => form.setSendInBatches(v === 'unchanged' ? false : v)}
            batchAmount={form.batchAmount}
            setBatchAmount={form.setBatchAmount}
            batchDelay={form.batchDelay}
            setBatchDelay={form.setBatchDelay}
            aiOptimizationEnabled={false}
            setAiOptimizationEnabled={() => {}}
            aiOptimizationType=""
            setAiOptimizationType={() => {}}
            aiOptimizationBatchAmount=""
            setAiOptimizationBatchAmount={() => {}}
            aiOptimizationMinimalValuePercent=""
            setAiOptimizationMinimalValuePercent={() => {}}
            aiOptimizationSendUntil=""
            setAiOptimizationSendUntil={() => {}}
            useOnlyNewDomains={form.useOnlyNewDomains}
            setUseOnlyNewDomains={(v) => form.setUseOnlyNewDomains(v === 'unchanged' ? false : v)}
            shortlink={{
              clickIdStructure: form.clickIdStructure,
              setClickIdStructure: (v) => { if (isShortlinkClickIdStructure(v)) form.setClickIdStructure(v); },
              domainScheme: form.domainScheme,
              setDomainScheme: (v) => { if (isShortlinkDomainScheme(v)) form.setDomainScheme(v); },
            }}
            isBusy={isBusy}
            compact
          />
        </div>
      </div>

      <div className="shrink-0 border-t border-border/40">
        <div className="flex items-center gap-1.5 bg-muted/20 px-4 py-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Static Tags</span>
        </div>
        <div className="p-3">
          <StaticTagsPickerSection
            mode="single"
            selectedId={form.selectedStaticTagId}
            onChange={form.setSelectedStaticTagId}
            disabled={isBusy}
            compact
            allowUnchanged={isMulti}
          />
        </div>
      </div>
    </div>
  );
}
